import { useMemo } from "react";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DemandAdjustmentType } from "../../types/demand-factor.type";
import { findOverlapWarnings, OverlapNames } from "./demandFactorOverlap";
import { DemandFactorFormValues, parseMultiplier } from "./formValues";
import { useOverlappingRows } from "./useOverlappingRows";

interface FormWarningsProps {
  values: DemandFactorFormValues;
  /** The row being edited, left out of the overlap check; null while creating. */
  editingId: number | null;
  /** Null until the list has told the page which types the calculation uses. */
  enabledTypes: DemandAdjustmentType[] | null;
  names: OverlapNames;
}

/** Advisory warnings under the form. None of them blocks saving. */
export default function FormWarnings({
  values,
  editingId,
  enabledTypes,
  names,
}: FormWarningsProps) {
  const { type, validFrom, validTo, targets } = values;
  const multiplier = parseMultiplier(values.multiplier);
  const overlappingRows = useOverlappingRows(type, validFrom, validTo);

  const overlapWarnings = useMemo(
    () =>
      findOverlapWarnings({ ...targets, id: editingId, multiplier }, overlappingRows, names),
    [targets, editingId, multiplier, overlappingRows, names],
  );

  const isTypeDisabled = type !== "" && enabledTypes !== null && !enabledTypes.includes(type);

  return (
    <Stack gap={1}>
      {multiplier === 0 && (
        <Alert severity="warning">ตัวคูณเป็น 0 — ยอดแนะนำจะเป็น 0</Alert>
      )}
      {isTypeDisabled && (
        <Alert severity="warning">
          ประเภทนี้ปิดอยู่ในการตั้งค่าการคำนวณ — จะไม่ถูกคำนวณจนกว่าจะเปิด
        </Alert>
      )}
      {overlapWarnings.length > 0 && (
        <Alert severity="warning">
          <Typography variant="body2" fontWeight={600} mb={0.5}>
            ช่วงวันที่และเป้าหมายซ้อนกับแถวประเภทเดียวกัน — ร้านที่โดนทั้งสองแถวจะถูกคูณทั้งคู่
          </Typography>
          {overlapWarnings.map((warning) => (
            <Typography key={warning.id} variant="body2">
              {warning.message}
            </Typography>
          ))}
        </Alert>
      )}
    </Stack>
  );
}
